/**
 * FileVersionHistoryModal Component
 * Displays version history for a file and allows restoring previous versions
 */

import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  ClockIcon,
  ArrowPathIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { latexAPI, type FileVersion } from '@api/latex'
import { logError, logDebug } from '@utils/logger'
import ConfirmDialog from '@components/common/ConfirmDialog'

interface FileVersionHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  documentId: string
  fileId: string
  fileName: string
  onVersionRestored?: () => void
}

export const FileVersionHistoryModal: React.FC<FileVersionHistoryModalProps> = ({
  isOpen,
  onClose,
  documentId,
  fileId,
  fileName,
  onVersionRestored
}) => {
  const [versions, setVersions] = useState<FileVersion[]>([])
  const [loading, setLoading] = useState(false)
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState<{ version: FileVersion | null }>({ version: null })

  useEffect(() => {
    if (isOpen && fileId) {
      loadVersions()
    }
  }, [isOpen, fileId])

  const loadVersions = async () => {
    setLoading(true)
    try {
      const response = await latexAPI.getFileVersions(documentId, fileId, 100, 0)
      setVersions(response.versions)
      logDebug('[FileVersionHistory] Loaded versions:', response.total)
    } catch (error) {
      logError('[FileVersionHistory] Failed to load versions:', error)
      toast.error('Failed to load version history')
    } finally {
      setLoading(false)
    }
  }

  const handleRestore = async (version: FileVersion) => {
    setRestoringVersion(version.version_number)
    try {
      await latexAPI.restoreFileVersion(
        documentId,
        fileId,
        version.version_number,
        true // Create new version before restoring
      )
      toast.success(`Restored to version ${version.version_number}`)
      setShowRestoreConfirm({ version: null })
      onVersionRestored?.()
      // Reload versions to show the new version created before restore
      await loadVersions()
    } catch (error) {
      logError('[FileVersionHistory] Failed to restore version:', error)
      toast.error('Failed to restore version')
    } finally {
      setRestoringVersion(null)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (!isOpen) return null

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Version History
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {fileName}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">Loading versions...</span>
                    </div>
                  ) : versions.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <ClockIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No version history available</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {versions.map((version) => (
                        <div
                          key={version.id}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-gray-900 dark:text-gray-100">
                                  Version {version.version_number}
                                </span>
                                {version.version_number === versions[0]?.version_number && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                                    Current
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                <div className="flex items-center gap-4">
                                  <span>{formatDate(version.created_at)}</span>
                                  <span>•</span>
                                  <span>{formatFileSize(version.file_size)}</span>
                                </div>
                                {version.change_description && (
                                  <p className="text-gray-500 dark:text-gray-500 italic">
                                    {version.change_description}
                                  </p>
                                )}
                              </div>
                            </div>
                            {version.version_number !== versions[0]?.version_number && (
                              <button
                                onClick={() => setShowRestoreConfirm({ version })}
                                disabled={restoringVersion === version.version_number}
                                className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                              >
                                {restoringVersion === version.version_number ? (
                                  <>
                                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                    Restoring...
                                  </>
                                ) : (
                                  <>
                                    <ArrowPathIcon className="w-4 h-4" />
                                    Restore
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Restore Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRestoreConfirm.version !== null}
        onClose={() => setShowRestoreConfirm({ version: null })}
        onConfirm={() => {
          if (showRestoreConfirm.version) {
            handleRestore(showRestoreConfirm.version)
          }
        }}
        title="Restore Version"
        message={
          showRestoreConfirm.version
            ? `Are you sure you want to restore this file to version ${showRestoreConfirm.version.version_number}? This will create a new version with the current content before restoring.`
            : ''
        }
        confirmText="Restore"
        cancelText="Cancel"
        confirmButtonClassName="bg-blue-600 hover:bg-blue-700"
      />
    </>
  )
}
